import React, { useState, useEffect } from "react";
import { FaEye, FaEyeSlash, FaCopy, FaKey } from "react-icons/fa";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { toast } from "react-toastify";
import axios from "axios";
import "react-toastify/dist/ReactToastify.css";

const APISettings = () => {
  const [apiKey, setApiKey] = useState("");
  const [apiEnabled, setApiEnabled] = useState(false);
  const [showFullApiKey, setShowFullApiKey] = useState(false);
  const [tallyIntegration, setTallyIntegration] = useState(false);
  const [tallyCompanyName, setTallyCompanyName] = useState("");
  const [attendanceTypeHead, setAttendanceTypeHead] = useState("");

  const apiUrl = process.env.REACT_APP_API_URL; // Assuming you have the API URL in your environment variables
  const token = localStorage.getItem("token"); // Assuming token is stored in localStorage

  useEffect(() => {
    // Fetch initial data when the component mounts
    fetchApiSettings();
  }, []);

  const fetchApiSettings = async () => {
    try {
      const response = await axios.get(`${apiUrl}/company/api-setting`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && response.data.length > 0) {
        const data = response.data[0];
        setApiKey(data.api_key || "");
        setApiEnabled(data.is_active === 1);
        setTallyIntegration(data.is_tally_integration === 1);
        setTallyCompanyName(data.tallycompanyname || "");
        setAttendanceTypeHead(data.tally_attendance_head || "");
      } else {
        setApiKey("");
        setApiEnabled(false);
        setTallyIntegration(false);
        setTallyCompanyName("");
        setAttendanceTypeHead("");
      }
    } catch (error) {
      console.error("Error fetching API settings:", error);
      toast.error("Failed to fetch API settings.");
    }
  };

  const handleApiToggle = async () => {
    const newStatus = !apiEnabled;
    setApiEnabled(newStatus);

    try {
      const response = await axios.post(
        `${apiUrl}/company/api-setting-update`,
        {
          api_key: apiKey,
          tallycompanyname: tallyCompanyName,
          tally_attendance_head: attendanceTypeHead,
          is_tally_integration: tallyIntegration,
          is_active: newStatus,
          isstatusupdate: true, // Indicating that this update is for status change
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.info(newStatus ? "API Key enabled!" : "API Key disabled!");
    } catch (error) {
      console.error("Error updating API status:", error);
      toast.error("Failed to update API status.");
    }
  };

  const handleToggle = () => {
    setTallyIntegration(!tallyIntegration);
  };

  const handleSave = async () => {
    if (tallyIntegration && (!tallyCompanyName || !attendanceTypeHead)) {
      toast.error("All fields are required when Tally integration is enabled.");
      return;
    }

    try {
      await axios.post(
        `${apiUrl}/company/api-setting-update`,
        {
          api_key: apiKey,
          tallycompanyname: tallyCompanyName,
          tally_attendance_head: attendanceTypeHead,
          is_tally_integration: tallyIntegration,
          is_active: apiEnabled,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Settings saved successfully!");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings.");
    }
  };

  const generateApiKey = () => {
    const generatedKey = `API${Math.random().toString(36).substr(2, 25)}${Math.random().toString(36).substr(2, 25)}AH62`;
    setApiKey(generatedKey);
    setApiEnabled(true);
    toast.success("API Key generated successfully!");
  };

  const handleCopyApiKey = () => {
    navigator.clipboard.writeText(apiKey);
    toast.success("API Key copied to clipboard!");
  };

  return (
    <div className="page-wrapper">
      {/* Page Content */}
      <div className="content container-fluid">
        {/* Page Header */}
        
        {/* /Page Header */}

        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <h3 className="card-title">API Key</h3>

                <p className="text-muted mb-3">
                  API Keys allow you to integrate with 3rd party applications for pushing and pulling data securely. Make sure to keep this key private.
                </p>

                <div className="d-flex align-items-center mb-3">
                  <div className="onoffswitch me-3">
                    <input
                      type="checkbox"
                      className="onoffswitch-checkbox"
                      id="switch_api_key"
                      checked={apiEnabled}
                      onChange={handleApiToggle}
                    />
                    <label
                      className="onoffswitch-label"
                      htmlFor="switch_api_key"
                    >
                      <span className="onoffswitch-inner" />
                      <span className="onoffswitch-switch" />
                    </label>
                  </div>
                  <span>{apiEnabled ? "API Enabled" : "API Disabled"}</span>
                </div>

                {apiEnabled && (
                  <>
                    <div className="input-group mb-3" style={{ maxWidth: '600px' }}>
                      <input
                        type="text"
                        className="form-control"
                        value={showFullApiKey ? apiKey : `${"*".repeat(apiKey.length - 4)}${apiKey.slice(-4)}`}
                        readOnly
                        style={{ backgroundColor: '#d6e4ff', width: '70%' }} // Adjust the width of the input
                      />
                      <button
                        className="btn btn-success"
                        onClick={() => setShowFullApiKey(!showFullApiKey)}
                        style={{ marginLeft: '5px' }}
                      >
                        {showFullApiKey ? <FaEyeSlash /> : <FaEye />}
                      </button>
                      <button
                        className="btn btn-success"
                        onClick={handleCopyApiKey}
                        style={{ marginLeft: '5px' }}
                      >
                        <FaCopy />
                      </button>
                      <button
                        className="btn btn-success"
                        onClick={generateApiKey}
                        style={{ marginLeft: '5px' }}
                      >
                        <FaKey />
                      </button>
                    </div>

                    <small className="text-muted">Click the button to generate a new API key. Copy the key for future use.</small>

                    <hr />

                    <div className="h3 card-title with-switch">
                      Tally Integration
                      <div className="onoffswitch">
                        <input
                          type="checkbox"
                          className="onoffswitch-checkbox"
                          id="switch_tally_integration"
                          checked={tallyIntegration}
                          onChange={handleToggle}
                        />
                        <label
                          className="onoffswitch-label"
                          htmlFor="switch_tally_integration"
                        >
                          <span className="onoffswitch-inner" />
                          <span className="onoffswitch-switch" />
                        </label>
                      </div>
                    </div>

                    {tallyIntegration && (
                      <>
                        <div className="mb-3">
                          <label className="form-label">Tally Company Name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={tallyCompanyName}
                            onChange={(e) => setTallyCompanyName(e.target.value)}
                            placeholder="Enter Tally Company Name"
                          />
                          <small className="text-muted">This should be the name of the Tally company you want to integrate.</small>
                        </div>

                        <div className="mb-3">
                          <label className="form-label">Attendance/Production Type Head</label>
                          <input
                            type="text"
                            className="form-control"
                            value={attendanceTypeHead}
                            onChange={(e) => setAttendanceTypeHead(e.target.value)}
                            placeholder="Enter Attendance/Production Type Head"
                          />
                          <small className="text-muted">This should match the name you have created inside Tally.</small>
                        </div>
                      </>
                    )}

                 <div className="d-flex justify-content-between align-items-center">
  <button
    className="btn btn-primary"
    onClick={handleSave}
  >
    Save Settings
  </button>

  <a
    href="http://secure.netshineoffice.com/tallyapp.tar" // Replace with the actual path to the Tally Utility
    className="btn btn-link"
    download
  >
   Download Tally integration tool
  </a>
</div>

                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Content */}
    </div>
  );
};

export default APISettings;
